import React, { FC } from 'react';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import css from '@styled-system/css';

import Icon from '../../Icon';

export interface SpinnerProps {
  size?: 'tiny' | 'small' | 'base' | 'medium' | 'large' | 'xlarge';
  color?: string;
  viewBox?: string;
}

const spin12 = keyframes`
to {
  transform: rotate(360deg);
}
`;

const StyledSpinner = styled(Icon)<SpinnerProps>(
  css({
    animation: `${spin12} 0.75s steps(12, end) infinite`,
    '&>path': {
      stroke: '#333',
      strokeWidth: 5,
      strokeLinecap: 'round',
    },
    '&>path:nth-of-type(12n+0)': {
      strokeOpacity: 0.08,
    },
    '&>path:nth-of-type(12n+1)': {
      strokeOpacity: 0.08,
    },
    '&>path:nth-of-type(12n+2)': {
      strokeOpacity: 0.17,
    },
    '&>path:nth-of-type(12n+3)': {
      strokeOpacity: 0.25,
    },
    '&>path:nth-of-type(12n+4)': {
      strokeOpacity: 0.33,
    },
    '&>path:nth-of-type(12n+5)': {
      strokeOpacity: 0.42,
    },
    '&>path:nth-of-type(12n+6)': {
      strokeOpacity: 0.5,
    },
    '&>path:nth-of-type(12n+7)': {
      strokeOpacity: 0.58,
    },
    '&>path:nth-of-type(12n+8)': {
      strokeOpacity: 0.66,
    },
    '&>path:nth-of-type(12n+9)': {
      strokeOpacity: 0.75,
    },
    '&>path:nth-of-type(12n+11)': {
      strokeOpacity: 0.92,
    },
  })
);

const Spinner: FC<SpinnerProps> = ({ color, size = 'medium', ...props }) => {
  return (
    <StyledSpinner viewBox="0 0 35 35" size={size} color={color} {...props}>
      <g fillRule="evenodd">
        <path
          d="M17.5 9c-.825 0-1.5-.675-1.5-1.5v-6c0-.825.675-1.5 1.5-1.5S19 .675 19 1.5v6c0 .825-.675 1.5-1.5 1.5"
          opacity={0.65}
        />
        <path
          d="M21.75 10.139a1.504 1.504 0 01-.549-2.05l3-5.195a1.504 1.504 0 012.049-.55c.715.413.962 1.335.549 2.05l-3 5.196a1.504 1.504 0 01-2.049.549"
          opacity={0.75}
        />
        <path
          d="M24.861 13.25a1.504 1.504 0 01.55-2.049l5.195-3a1.504 1.504 0 012.05.549 1.504 1.504 0 01-.55 2.049l-5.196 3a1.504 1.504 0 01-2.049-.549"
          opacity={0.85}
        />
        <path
          d="M26 17.5c0-.825.675-1.5 1.5-1.5h6c.825 0 1.5.675 1.5 1.5s-.675 1.5-1.5 1.5h-6c-.825 0-1.5-.675-1.5-1.5"
          opacity={0.9}
        />
        <path
          d="M24.861 21.75a1.504 1.504 0 012.05-.549l5.195 3c.715.412.962 1.334.55 2.049a1.504 1.504 0 01-2.05.549l-5.196-3a1.504 1.504 0 01-.549-2.049"
          opacity={0.2}
        />
        <path
          d="M21.75 24.861a1.504 1.504 0 012.049.55l3 5.195a1.504 1.504 0 01-.549 2.05 1.504 1.504 0 01-2.049-.55l-3-5.196a1.504 1.504 0 01.549-2.049"
          opacity={0.25}
        />
        <path
          d="M17.5 26c.825 0 1.5.675 1.5 1.5v6c0 .825-.675 1.5-1.5 1.5s-1.5-.675-1.5-1.5v-6c0-.825.675-1.5 1.5-1.5"
          opacity={0.3}
        />
        <path
          d="M13.25 24.861c.714.413.961 1.335.549 2.05l-3 5.195a1.504 1.504 0 01-2.049.55 1.504 1.504 0 01-.549-2.05l3-5.196a1.504 1.504 0 012.049-.549"
          opacity={0.35}
        />
        <path
          d="M10.139 21.75a1.504 1.504 0 01-.55 2.049l-5.195 3a1.504 1.504 0 01-2.05-.549 1.504 1.504 0 01.55-2.049l5.196-3a1.504 1.504 0 012.049.549"
          opacity={0.4}
        />
        <path
          d="M9 17.5c0 .825-.675 1.5-1.5 1.5h-6C.675 19 0 18.325 0 17.5S.675 16 1.5 16h6c.825 0 1.5.675 1.5 1.5"
          opacity={0.45}
        />
        <path
          d="M10.139 13.25a1.504 1.504 0 01-2.05.549l-5.195-3a1.504 1.504 0 01-.55-2.049 1.504 1.504 0 012.05-.549l5.196 3c.714.412.961 1.335.549 2.049"
          opacity={0.5}
        />
        <path
          d="M13.25 10.139a1.504 1.504 0 01-2.049-.55l-3-5.195a1.504 1.504 0 01.549-2.05 1.504 1.504 0 012.049.55l3 5.196a1.504 1.504 0 01-.549 2.049"
          opacity={0.55}
        />
      </g>
    </StyledSpinner>
  );
};

export default Spinner;
